import { Fragment, memo, useMemo } from 'react'
import classNames from 'classnames'

import {
  IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
  IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD
} from '@aytm/lib/common/assets'

import { imageAsset as imageAssetUrl } from '@aytm/app/survey/editor/resources/urls'
import { questionPositionToVirtualPosition } from '@aytm/app/stats/page/comments/lib/utils'

import { TableDataViewCell, TableDataViewCellTitle } from '@aytm/app/stats/page/question-views/components/table/types'

import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import ImagePreview from '@aytm/app/survey/editor/components/content/assets/ImagePreview'

// styles
import style from '@aytm/app/stats/page/question-views/components/table/styles/Table.module.scss'

const TITLE_IMAGE_THUMB_MIN = 70
const TITLE_IMAGE_THUMB_MAX = 265

const TITLE_THUMB_WIDTH = 23
const TITLE_THUMB_HEIGHT = 23

type TableCellTitleProps = {
  cell: TableDataViewCell
  width: number | string
  position: number
  format?: (value: number | string) => number | string
}

type StylesProps = {
  flexGrow?: number
  flexShrink?: number
  flexBasis?: string
  width?: string
  borderLeftColor?: string
}

export default memo(TableCellTitle)
export function TableCellTitle(props: TableCellTitleProps) {
  const cell = props.cell as TableDataViewCellTitle

  const styles = useMemo(() => {
    const values: StylesProps = {}
    if ('width' in props && Number.isFinite(props.width)) {
      values.flexGrow = 0
      values.flexShrink = 0
      values.flexBasis = `${props.width}px`
      values.width = `${props.width}px`
    }
    if ('width' in props && 'string' == typeof props.width) {
      values.flexGrow = 0
      values.flexShrink = 0
      values.flexBasis = props.width
      values.width = props.width
    }
    if ('color' in cell) {
      values.borderLeftColor = cell.color
    }
    return values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.width, cell.color])

  const renderSkipsTo = () => (
    <>
      {1 == cell.skips_to.length ? null : <i className="icon-aytmfd-random" />}
      {cell.skips_to.map((item, index) => (
        <Fragment key={index + 1}>
          {0 < index ? ' or ' : ''}
          {-1 == item ? (
            <span className={style['row-cell-title-skips_to-exit']}>
              {1 == cell.skips_to.length ? <i className="icon-aytmfd-skip" /> : null}EXIT
            </span>
          ) : -99 == item ? (
            <span className={style['row-cell-title-skips_to-exit']}>
              {1 == cell.skips_to.length ? <i className="icon-aytmfd-skip" /> : null}HIDDEN EXIT
            </span>
          ) : (
            <span
              className={style['row-cell-title-skips_to-item']}
              onClick={'onSkipsToClick' in cell ? () => cell.onSkipsToClick(item) : null}
            >
              {1 == cell.skips_to.length ? <i className="icon-aytmfd-skip" /> : null}
              {questionPositionToVirtualPosition(item)}
            </span>
          )}
        </Fragment>
      ))}
    </>
  )

  return (
    <div
      className={classNames(
        style['row-cell'],
        style['row-cell-title'],
        'classNames' in cell ? cell.classNames.map(className => style[className]).join(' ') : '',
        { [style['row-cell-title-active']]: 'onClick' in cell, [`color-${cell.colorIndex}`]: 'colorIndex' in cell }
      )}
      title={'tooltip' in cell ? cell.tooltip : null}
      style={styles}
      onClick={'onClick' in cell ? () => cell.onClick(props.position) : null}
    >
      {'image' in cell ? (
        <div className={style['row-cell-title-thumbnail']}>
          <Popup
            portal
            trigger="hover"
            direction="left"
            content={() => (
              <ImagePreview
                minWidth={`${TITLE_IMAGE_THUMB_MIN}px`}
                maxWidth={`${TITLE_IMAGE_THUMB_MAX}px`}
                minHeight={`${TITLE_IMAGE_THUMB_MIN}px`}
                maxHeight={`${TITLE_IMAGE_THUMB_MAX}px`}
                imageUrl={cell.image.original.url}
                previewUrl={imageAssetUrl(cell.image.original.path, {
                  width: TITLE_IMAGE_THUMB_MAX,
                  height: TITLE_IMAGE_THUMB_MAX,
                  filter: IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
                  unsharp: true,
                  backgroundColor: 'transparent'
                })}
              />
            )}
            contentTheme="thin-black"
            hideOtherPopupsOnShow
          >
            <img
              src={imageAssetUrl(cell.image.original.path, {
                width: TITLE_THUMB_WIDTH,
                height: TITLE_THUMB_HEIGHT,
                filter: IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD,
                unsharp: true,
                backgroundColor: 'white'
              })}
            />
          </Popup>
        </div>
      ) : null}
      {'label' in cell ? (
        <span
          className={classNames(style['row-cell-title-label'], {
            [style['row-cell-title-label-active']]: 'onLabelClick' in cell
          })}
          onClick={'onLabelClick' in cell ? () => cell.onLabelClick(props.position) : null}
        >
          {cell.label}:
        </span>
      ) : null}
      <div className={style['row-cell-title-text']} dangerouslySetInnerHTML={{ __html: cell.title }} />
      {'comments' in cell ? (
        <div
          className={classNames(style['row-cell-title-comments'], {
            [style['row-cell-title-comments-disabled']]: 0 == cell.comments
          })}
          onClick={
            0 < cell.comments && 'onCommentsClick' in cell ? event => cell.onCommentsClick(event, props.position) : null
          }
        >
          <span className={style['row-cell-title-comments-count']}>{cell.comments}</span>
          <span className={style['row-cell-title-comments-icon']} title={`View ${cell.comments} custom answers`}>
            <i className="icon-aytmfd-comments-v2" />
          </span>
        </div>
      ) : null}
      {'skips_to' in cell ? <div className={style['row-cell-title-skips_to']}>{renderSkipsTo()}</div> : null}
      {'appendix' in cell ? (
        <span className={style['row-cell-title-appendix']} dangerouslySetInnerHTML={{ __html: cell.appendix }} />
      ) : null}
    </div>
  )
}
